/**
 * ForumImageUploader — small button + preview strip used inside both the
 * NewTopicDialog composer and the reply box on CommunityTopic. On upload,
 * the returned URL is appended to the parent's body as a markdown image
 * `![alt](url)` so it renders inline when the post is displayed.
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { ImagePlus, Loader2, X } from 'lucide-react';

const ForumImageUploader = ({ value = [], onChange, testIdPrefix = 'forum-img', compact = false }) => {
  const { API } = useContext(AuthContext);
  const [uploading, setUploading] = useState(false);

  const handlePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/uploads/forum`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({ url: r.data.url, filename: r.data.filename, content_type: r.data.content_type });
      }
      onChange?.([...(value || []), ...uploaded]);
      toast.success(uploaded.length > 1 ? `${uploaded.length} images uploaded` : 'Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const remove = (idx) => {
    onChange?.((value || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label
          className={`inline-flex items-center gap-1.5 rounded-full bg-primary/10 hover:bg-primary/15 text-primary text-xs font-bold cursor-pointer transition-colors ${compact ? 'h-7 px-2.5' : 'h-8 px-3'}`}
          data-testid={`${testIdPrefix}-label`}
        >
          {uploading
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
            : <><ImagePlus className="w-3.5 h-3.5" /> Add image</>}
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            disabled={uploading}
            onChange={handlePick}
            data-testid={`${testIdPrefix}-input`}
          />
        </label>
        <span className="text-[10px] text-muted-foreground">JPG / PNG / GIF · up to 25MB</span>
      </div>

      {value?.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testIdPrefix}-previews`}>
          {value.map((img, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-border bg-muted aspect-square">
              <img src={img.url} alt={img.filename} className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove image"
                data-testid={`${testIdPrefix}-remove-${i}`}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ForumImageUploader;
